import { useEffect, useState } from "react";
import { CheckCircle2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { InterestForm } from "@/components/InterestForm";
import { ServiceRequestForm } from "@/components/ServiceRequestForm";
import { FadeIn } from "@/components/Reveal";

const PLANNED = [
  { title: "Electronic fingerprinting", desc: "Preparation for electronic fingerprint capture to support identity verification needs." },
  { title: "Employment background checks", desc: "Designed to support eligible employment background check requirements." },
  { title: "Connected to TSM screening", desc: "Planned to work alongside TSM Background Screening and workforce verification services." },
];

export default function FingerprintingPage() {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    document.title = "TSM Fingerprinting | Coming Soon | TSM Group CLE";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "TSM Group CLE is preparing to expand into electronic fingerprinting services to support employment screening, identity verification, and applicable background check requirements. Join the interest list.");
  }, []);

  return (
    <div className="bg-paper" data-testid="fingerprinting-page">
      <PageHero
        testid="fingerprinting-hero"
        eyebrow="TSM4 — In Preparation"
        title={<>TSM Fingerprinting <span className="text-gold">| Coming Soon</span></>}
        copy="TSM Group CLE is preparing to expand into electronic fingerprinting services to support employment screening, identity verification, and applicable background check requirements. Join our interest list to receive updates when services become available."
      >
        <div className="mt-9">
          <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-teal/60 bg-teal/10 px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-teal" data-testid="fingerprinting-status-chip">
            <Clock className="h-3.5 w-3.5" />
            Coming Soon — Not Currently Operational
          </span>
        </div>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="container-tsm max-w-[1200px]">
          <FadeIn className="max-w-3xl">
            <p className="eyebrow">What We're Preparing</p>
            <h2 className="mt-6 text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl" data-testid="fingerprinting-planned-headline">
              Identity verification, the TSM way.
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 sm:grid-cols-3" data-testid="fingerprinting-planned-grid">
            {PLANNED.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.07} className="h-full">
                <div className="h-full rounded-xl border border-navy/10 bg-white p-7">
                  <CheckCircle2 className="h-5 w-5 text-teal" />
                  <h3 className="mt-4 text-lg font-extrabold text-navy">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.15}>
            <p className="mt-10 max-w-3xl text-sm italic leading-relaxed text-ink/55">
              A launch date has not been confirmed. Bookings and payments for fingerprinting will only open when the
              service officially launches.
            </p>
          </FadeIn>
        </div>
      </section>

      {status === "available" ? (
        <ServiceRequestForm defaultService="Fingerprinting" />
      ) : (
        <InterestForm
          interestType="fingerprinting"
          heading="Join the Fingerprinting Waitlist."
          intro="Tell us about your business and your anticipated fingerprinting needs. We'll contact you with updates when the service becomes available."
          submitLabel="Join the Fingerprinting Waitlist"
          testidPrefix="fingerprinting-interest"
        />
      )}
    </div>
  );
}
